/**
 * Registers `public/sw.js` (owner requirement 2026-09-26, requirement 2a)
 * under this page's own base path — `import.meta.env.BASE_URL` is Vite's
 * own resolved `base` (`src/config.ts`'s `VITE_BASE_PATH`, default
 * `/observatory/`), so the registration scope always matches wherever the
 * Futarchists site actually mounts this bundle, never a hardcoded path.
 *
 * A browser without service worker support (or one running the page over
 * plain HTTP on a non-localhost origin, where service workers are refused)
 * degrades to "no offline cache" — every data path already works without
 * one (IndexedDB + the in-memory LRU still apply; only the *network layer's*
 * cache-first/network-first policy is unavailable), so a failed
 * registration is logged, never thrown.
 */

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const base = import.meta.env.BASE_URL;
    return await navigator.serviceWorker.register(`${base}sw.js`, { scope: base });
  } catch (error) {
    console.warn("observatory: service worker registration failed", error);
    return null;
  }
}

/** The message `public/sw.js` accepts to prune superseded bundles (FIX3). */
export interface BuildAnnouncement {
  readonly type: "obs-build";
  readonly entry: string;
  readonly keep: readonly string[];
}

/** The `/assets/` URLs this page has actually loaded (entry, CSS, lazy chunks so far). */
export function loadedAssetUrls(entries: readonly { name: string }[]): string[] {
  return entries.map((entry) => entry.name).filter((name) => name.includes("/assets/"));
}

export function buildAnnouncement(
  entry: string,
  entryUrl: string,
  loaded: readonly { name: string }[],
): BuildAnnouncement {
  return { type: "obs-build", entry, keep: [...new Set([entryUrl, ...loadedAssetUrls(loaded)])] };
}

/**
 * Tells the active service worker which build this page runs (FIX3). The worker
 * remembers the last announced entry; on a CHANGE it drops every cached
 * `/assets/` bundle this page is not using, so superseded immutable bundles do
 * not linger in Cache Storage forever (they are content-hashed, so the
 * cache-first policy itself is unchanged and stays correct).
 */
export async function announceBuildToServiceWorker(entry: string, entryUrl: string): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.ready;
    registration.active?.postMessage(
      buildAnnouncement(entry, entryUrl, performance.getEntriesByType("resource")),
    );
  } catch (error) {
    console.warn("observatory: could not announce the build to the service worker", error);
  }
}
